import { getLocalTimeZone } from '@internationalized/date';
import { useMemo } from 'react';
import { LuSearch } from 'react-icons/lu';
import { Button, DatePicker, Input, toaster } from '@/core/ui';
import { useWeeklySqlFilters } from '@/store';
import { parseDateValue } from '@/utils/date';

export type WeeklyRecordsFilters = {
  startDate: string | null;
  endDate: string | null;
  dislikesThreshold: number | undefined;
  percentageLessThan: number | undefined;
  percentageGreaterThan: number | undefined;
};

type GenerationFormProps = {
  onGenerate: (filters: WeeklyRecordsFilters) => void;
  isLoading?: boolean;
};

export const GenerationForm = ({ onGenerate, isLoading = false }: GenerationFormProps) => {
  const startDateStr = useWeeklySqlFilters.use.startDate();
  const endDateStr = useWeeklySqlFilters.use.endDate();
  const dislikeThreshold = useWeeklySqlFilters.use.dislikesThreshold();
  const percentageLessThan = useWeeklySqlFilters.use.percentageLessThan();
  const percentageGreaterThan = useWeeklySqlFilters.use.percentageGreaterThan();
  const setStartDate = useWeeklySqlFilters.use.setStartDate();
  const setEndDate = useWeeklySqlFilters.use.setEndDate();
  const setDislikesThreshold = useWeeklySqlFilters.use.setDislikesThreshold();
  const setPercentageLessThan = useWeeklySqlFilters.use.setPercentageLessThan();
  const setPercentageGreaterThan = useWeeklySqlFilters.use.setPercentageGreaterThan();
  const setFilters = useWeeklySqlFilters.use.setFilters();

  const startDate = useMemo(() => parseDateValue(startDateStr), [startDateStr]);
  const endDate = useMemo(() => parseDateValue(endDateStr), [endDateStr]);

  const handleSubmit = () => {
    if (!startDate || !endDate) {
      toaster.error('Please select both start date and end date');
      return;
    }

    const hasDislikeThreshold = dislikeThreshold && !Number.isNaN(dislikeThreshold);
    const hasPercentageLessThan = percentageLessThan && !Number.isNaN(percentageLessThan);
    const hasPercentageGreaterThan = percentageGreaterThan && !Number.isNaN(percentageGreaterThan);

    if (!hasDislikeThreshold && !hasPercentageLessThan && !hasPercentageGreaterThan) {
      toaster.error('Please enter at least one filter: dislike threshold, % <, or % >');
      return;
    }

    // TODO: Once backend supports percentage filters, remove this check
    if (!hasDislikeThreshold && (hasPercentageLessThan || hasPercentageGreaterThan)) {
      toaster.error(
        'Percentage filters (% < and % >) are not yet supported by the backend. Please also provide a dislike threshold.',
      );
      return;
    }

    // Validate individual inputs if provided
    if (dislikeThreshold && Number.isNaN(Number(dislikeThreshold))) {
      toaster.error('Please enter a valid dislike threshold');
      return;
    }

    if (percentageLessThan && Number.isNaN(Number(percentageLessThan))) {
      toaster.error('Please enter a valid % < value');
      return;
    }

    if (percentageGreaterThan && Number.isNaN(Number(percentageGreaterThan))) {
      toaster.error('Please enter a valid % > value');
      return;
    }

    setFilters({
      startDate: startDateStr,
      endDate: endDateStr,
      dislikesThreshold: hasDislikeThreshold ? dislikeThreshold : null,
      percentageLessThan: hasPercentageLessThan ? percentageLessThan : null,
      percentageGreaterThan: hasPercentageGreaterThan ? percentageGreaterThan : null,
    });

    const startDateISO = startDate.toDate(getLocalTimeZone()).toISOString();
    const endDateISO = endDate.toDate(getLocalTimeZone()).toISOString();

    onGenerate({
      startDate: startDateISO,
      endDate: endDateISO,
      dislikesThreshold: hasDislikeThreshold ? (dislikeThreshold ?? undefined) : undefined,
      percentageLessThan: hasPercentageLessThan ? (percentageLessThan ?? undefined) : undefined,
      percentageGreaterThan: hasPercentageGreaterThan ? (percentageGreaterThan ?? undefined) : undefined,
    });
  };

  const hasAnyFilter =
    (dislikeThreshold && !Number.isNaN(dislikeThreshold)) ||
    (percentageLessThan && !Number.isNaN(percentageLessThan)) ||
    (percentageGreaterThan && !Number.isNaN(percentageGreaterThan));

  const handleDislikesThresholdChange = (value: string) => {
    setDislikesThreshold(value === '' || Number.isNaN(Number(value)) ? null : Number(value));
  };

  const handlePercentageLessThanChange = (value: string) => {
    setPercentageLessThan(value === '' || Number.isNaN(Number(value)) ? null : Number(value));
  };

  const handlePercentageGreaterThanChange = (value: string) => {
    setPercentageGreaterThan(value === '' || Number.isNaN(Number(value)) ? null : Number(value));
  };

  return (
    <div className='bg-background rounded-lg shadow-md p-4'>
      <h2 className='text-lg font-medium text-default-700 mb-4'>Set Filters for Weekly SQL Search</h2>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-wrap gap-4 items-end'>
          <DatePicker
            label='Start Date'
            labelPlacement='inside'
            value={startDate ?? undefined}
            onChange={(value) => setStartDate(value?.toString() ?? null)}
            className='w-52'
            variant='faded'
            size='lg'
            showMonthAndYearPickers
            granularity='day'
          />
          <DatePicker
            label='End Date'
            labelPlacement='inside'
            value={endDate ?? undefined}
            onChange={(value) => setEndDate(value?.toString() ?? null)}
            className='w-52'
            variant='faded'
            size='lg'
            showMonthAndYearPickers
            granularity='day'
          />

          <div className='flex flex-wrap gap-4 items-end ml-4'>
            <div className='flex items-center gap-2'>
              <Input
                label='Dislike Threshold'
                labelPlacement='inside'
                type='number'
                value={dislikeThreshold ? String(dislikeThreshold) : ''}
                onValueChange={handleDislikesThresholdChange}
                placeholder='Enter threshold'
                className='w-52'
                variant='faded'
                size='lg'
              />
              <span className='text-default-500 font-medium mb-2'>AND</span>
            </div>
            <div className='flex items-center gap-2'>
              <Input
                label='Percentage less than'
                labelPlacement='inside'
                type='number'
                value={percentageLessThan ? String(percentageLessThan) : ''}
                onValueChange={handlePercentageLessThanChange}
                placeholder='Enter %'
                className='w-52'
                variant='faded'
                size='lg'
              />
              <span className='text-default-500 font-medium mb-2'>OR</span>
            </div>
            <Input
              label='Percentage greater than'
              labelPlacement='inside'
              type='number'
              value={percentageGreaterThan ? String(percentageGreaterThan) : ''}
              onValueChange={handlePercentageGreaterThanChange}
              placeholder='Enter %'
              className='w-52'
              variant='faded'
              size='lg'
            />
          </div>

          <div className='flex justify-end'>
            <Button
              color='primary'
              size='lg'
              isIconOnly
              onPress={handleSubmit}
              isLoading={isLoading}
              className='size-16'
              isDisabled={!startDate || !endDate || !hasAnyFilter || isLoading}
            >
              <LuSearch className='size-5' />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
